"use client";

import * as React from "react";
import { cn } from "../utils";
import { Icon } from "../icon";
import {
  cursorAreaVariants,
  cursorElementVariants,
} from "./custom-cursor.variants";
import type {
  CustomCursorAreaProps,
  CustomCursorType,
} from "./custom-cursor.types";

function CursorElement({ type }: { type: CustomCursorType }) {
  if (type === "play") {
    return (
      <div className={cursorElementVariants({ type: "play" })}>
        <Icon name="play-arrow" size="lg" className="text-current" />
      </div>
    );
  }

  return (
    <div className={cursorElementVariants({ type: "drag" })}>
      <Icon name="chevron-left" size="lg" className="text-current" />
      <Icon name="chevron-right" size="lg" className="text-current" />
    </div>
  );
}

/**
 * Custom Cursor Area
 *
 * V&A Waterfront Design System - Custom Cursor
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=12358-51481
 *
 * Wraps content with a custom branded cursor that replaces the native cursor.
 * Supports "drag" (chevrons for carousels) and "play" (play icon for videos).
 *
 * @example
 * <CustomCursorArea type="drag">
 *   <Carousel>...</Carousel>
 * </CustomCursorArea>
 */
const CustomCursorArea = React.forwardRef<
  HTMLDivElement,
  CustomCursorAreaProps
>(({ type, children, className }, ref) => {
  const cursorRef = React.useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const [isOverInteractive, setIsOverInteractive] = React.useState(false);
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);
  const positionRef = React.useRef({ x: 0, y: 0 });
  const rafRef = React.useRef<number>(0);

  React.useEffect(() => {
    const hasHoverPointer = window.matchMedia(
      "(any-hover: hover) and (any-pointer: fine)",
    ).matches;
    setIsTouchDevice(!hasHoverPointer);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const updateCursorPosition = React.useCallback(() => {
    if (cursorRef.current) {
      cursorRef.current.style.transform = `translate(${positionRef.current.x}px, ${positionRef.current.y}px)`;
    }
  }, []);

  const handleMouseMove = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const rect = container.getBoundingClientRect();
      positionRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      setIsOverInteractive(
        Boolean(
          (e.target as HTMLElement).closest(
            'a, button, [role="button"], input, select, textarea, label',
          ),
        ),
      );

      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateCursorPosition);
    },
    [updateCursorPosition],
  );

  const handleMouseEnter = React.useCallback(() => {
    setIsVisible(true);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    setIsVisible(false);
    setIsOverInteractive(false);
  }, []);

  if (isTouchDevice) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        cursorAreaVariants(),
        isOverInteractive && "cursor-auto",
        className,
      )}
      data-testid="custom-cursor-area"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      <div
        ref={cursorRef}
        className={cn(
          "pointer-events-none absolute left-0 top-0 z-50 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-150",
          isVisible && !isOverInteractive ? "opacity-100" : "opacity-0",
        )}
        aria-hidden="true"
      >
        <CursorElement type={type} />
      </div>
    </div>
  );
});

CustomCursorArea.displayName = "CustomCursorArea";

export { CustomCursorArea };
